import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { Plus, Search, GripVertical, MoreHorizontal, Loader2, MessageSquare, Pencil, Trash2 } from 'lucide-react';
import AddQuestionModal from './AddQuestionModal'
import AddCategoryModal from './AddCategoryModal'
import { selectSelectedTask } from '../../../slice/taskSlice';
import { createStandardQuestion, fetchSQByTaskId, selectSQ, selectSQLoading, selectSQError, deleteStandardQuestion } from '../../../slice/sqSlice';
import DeleteModal from '../../../components/atomic/DeleteModal';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { reorderSQ } from '../../../Api/sqApi';
import Loader from '../../../components/atomic/Loader';
import { useThemeColor } from '../../../hooks/useThemeColor';
import axios from 'axios';
import { BASE_URL } from '../../../components/constants';
import { useTranslation } from '../../../i18n/LanguageContext';
import { fetchAgentLayout, updateShowProjectDetail } from '../../../slice/agentWidgetsSlice';
import { authHeaders } from '../../../utils/authHeaders';

const StandarQustions = () => {
  const { t } = useTranslation();
  const { aispaceColor } = useThemeColor();
  const { data } = useSelector((state) => state.admin);

  const isAdmin = data?.user_role === "SUPER_ADMIN" || data?.user_role === "ADMIN";
  const isSuperAdmin = data?.user_role === "SUPER_ADMIN" || data?.user_role === "ADMIN";
  const userOrganizationId = data?.organization_id;
  const dispatch = useDispatch();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [questionToDelete, setQuestionToDelete] = useState(null);
  const [reorderLoading, setReorderLoading] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  const [categories, setCategories] = useState([]);
  const [hasFetched, setHasFetched] = useState(false);
  const [isReorderMode, setIsReorderMode] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const selectedTask = useSelector(selectSelectedTask);
  const questions = useSelector(selectSQ);
  const sqLoading = useSelector(selectSQLoading);
  useSelector(selectSQError); // Error state available if needed

  const loading = !hasFetched || sqLoading;

  const { showProjectDetailByTask, layoutLoadingByTask, layoutByTask } = useSelector((s) => s.agentWidgets);
  const taskId = selectedTask?.id?.toString();
  const isLayoutLoading = taskId ? layoutLoadingByTask[taskId] === true : false;
  // Use strict equality: undefined (not loaded) = false; prevents flicker ON→OFF
  const showProjectDetail = taskId
    ? (showProjectDetailByTask[taskId] === true)
    : false;

  // Fetch questions and categories whenever selectedTask changes
  const fetchCategories = async () => {
    if (!selectedTask?.id) return;
    try {
      const res = await axios.get(`${BASE_URL}/categories/${selectedTask.id}`, authHeaders());
      setCategories(res.data.categories || []);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  useEffect(() => {
    if (selectedTask?.id) {
      setHasFetched(false);
      dispatch(fetchSQByTaskId(selectedTask.id)).finally(() => {
        setHasFetched(true);
      });
      fetchCategories();
      const tid = selectedTask.id.toString();
      if (!layoutLoadingByTask[tid] && !layoutByTask[tid]) {
        dispatch(fetchAgentLayout(tid));
      }
    }
  }, [selectedTask?.id, dispatch, layoutLoadingByTask, layoutByTask]);

  // Reset page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const handleAddQuestion = async (questionData) => {
    if (!selectedTask?.id) {
      console.error('No task selected');
      return;
    }

    if (isAdmin && !isSuperAdmin) {
      if (selectedTask.organization_id && selectedTask.organization_id !== userOrganizationId) {
        console.error('ADMIN users can only add questions to tasks assigned to their organization');
        return;
      }
    }

    try {
      await dispatch(createStandardQuestion({
        ...questionData,
        task_id: selectedTask.id
      })).unwrap();
      dispatch(fetchSQByTaskId(selectedTask.id));
      setIsModalOpen(false);
    } catch (err) {
      console.error('Failed to create question:', err);
    }
  };

  const handleDelete = (id) => {
    if (isAdmin && !isSuperAdmin) {
      if (selectedTask.organization_id && selectedTask.organization_id !== userOrganizationId) {
        console.error('ADMIN users can only delete questions from tasks assigned to their organization');
        return;
      }
    }

    setQuestionToDelete(id);
    setIsDeleteModalOpen(true);
    setActiveMenu(null);
  };

  const confirmDelete = async () => {
    try {
      await dispatch(deleteStandardQuestion(questionToDelete)).unwrap();
    } catch (error) {
      console.error('Delete error:', error);
    }
    setIsDeleteModalOpen(false);
    setQuestionToDelete(null);
  };

  const handleQuestionClick = (question) => {
    setSelectedQuestion(question);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedQuestion(null);
    setIsModalOpen(false);
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return '-';
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength) + '...';
  };

  const filteredQuestions = questions.filter(question =>
    question?.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    question?.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    question?.category?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Pagination calculations
  const totalPages = Math.ceil(filteredQuestions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedQuestions = filteredQuestions.slice(startIndex, startIndex + itemsPerPage);

  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;

    const currentOrder = questions.map(q => q.id);
    const reordered = Array.from(questions);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);
    const newOrder = reordered.map(q => q.id);
    const isSameOrder = currentOrder.every((id, idx) => id === newOrder[idx]);
    if (isSameOrder) return;

    setReorderLoading(true);
    const orderList = reordered.map((q, idx) => ({ id: q.id, order: idx + 1 }));
    await reorderSQ(orderList);
    await dispatch(fetchSQByTaskId(selectedTask.id));
    setReorderLoading(false);
  };

  if (!selectedTask?.id) {
    return (
      <div className="py-4 text-center text-gray-500">
        {t('agentSettings.questionsSelectTaskHint')}
      </div>
    );
  }

  const renderRow = (question, _index, provided = null, isLast = false) => (
    <div
      ref={provided?.innerRef}
      {...(provided?.draggableProps || {})}
      className={`flex items-center hover:bg-[#F9FAFB] cursor-pointer ${!isLast ? 'border-b border-[#EEEEEE]' : ''}`}
      onClick={() => handleQuestionClick(question)}
    >
      {/* Drag Handle - only interactive/visible in reorder mode */}
      <div
        className="w-[50px] flex-shrink-0 py-4 px-4"
        {...(provided?.dragHandleProps || {})}
      >
        {isReorderMode && <GripVertical size={18} className="text-[#9CA3AF]" />}
      </div>

      {/* Categories */}
      <div className="w-[140px] flex-shrink-0 py-4 pr-4">
        <span className="text-[14px] text-[#374151]">
          {question.category || '-'}
        </span>
      </div>

      {/* Question Title */}
      <div className="w-[280px] flex-shrink-0 py-4 pr-4">
        <span className="text-[14px] text-[#374151]">
          {truncateText(question.title, 60)}
        </span>
      </div>

      {/* Description */}
      <div className="flex-1 py-4 pr-4">
        <span className="text-[14px] text-[#6B7280]">
          {truncateText(question.description, 80)}
        </span>
      </div>

      {/* Actions */}
      <div className="w-[50px] flex-shrink-0 py-4 px-4 relative">
        {isSuperAdmin && (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setActiveMenu(activeMenu === question.id ? null : question.id);
              }}
              className="p-1.5 border border-[#E5E7EB] hover:bg-[#F3F4F6] rounded-lg transition-colors"
            >
              <MoreHorizontal size={18} className="text-[#6B7280]" />
            </button>

            {activeMenu === question.id && (
              <div className={`absolute right-4 ${isLast ? 'bottom-12' : 'top-12'} bg-white border border-[#E5E7EB] rounded-lg shadow-lg z-50 min-w-[140px] py-1`}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleQuestionClick(question);
                    setActiveMenu(null);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-[14px] text-[#374151] hover:bg-[#F9FAFB] transition-colors"
                >
                  <Pencil size={16} className="text-[#6B7280]" />
                  {t('agentSettings.edit')}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(question.id);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-[14px] text-[#EF4444] hover:bg-[#FEF2F2] transition-colors"
                >
                  <Trash2 size={16} />
                  {t('agentSettings.delete')}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );

  return (
    <div onClick={() => setActiveMenu(null)}>
      {/* Project Detail toggle */}
      <div className="flex items-center justify-between bg-white border border-[#E5E7EB] rounded-xl px-5 py-4 mb-6">
        <div className="min-w-0 pr-4">
          <h2 className="text-[15px] font-semibold text-[#212121]">{t('agentSettings.showProjectDetailTitle')}</h2>
          <p className="text-[13px] text-[#6B7280] mt-1">{t('agentSettings.showProjectDetailSubtitle')}</p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={showProjectDetail}
          disabled={isLayoutLoading}
          onClick={() => !isLayoutLoading && dispatch(updateShowProjectDetail({ taskId, show: !showProjectDetail }))}
          className="relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ backgroundColor: showProjectDetail ? aispaceColor : '#E5E7EB' }}
        >
          <span
            className="inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform"
            style={{ transform: showProjectDetail ? 'translateX(22px)' : 'translateX(2px)' }}
          />
        </button>
      </div>

      {/* Header Actions */}
      <div className="flex items-center gap-4 mb-6">
        {isReorderMode ? (
          <p className="flex-1 text-[13px] text-[#6B7280]">{t('agentSettings.questionsReorderHint')}</p>
        ) : (
          <>
            {/* Search */}
            <div className="relative w-full max-w-md">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('agentSettings.searchPlaceholder')}
                className="w-full pl-10 pr-4 py-2.5 border border-[#E5E7EB] rounded-lg text-[14px] focus:outline-none focus:border-[var(--focus-color)] transition-colors"
                style={{ '--focus-color': aispaceColor }}
              />
            </div>

            <div className="flex-1"></div>
          </>
        )}

        {/* Reorder / Done Button */}
        {isSuperAdmin && (
          <button
            onClick={() => setIsReorderMode((prev) => !prev)}
            className="px-4 py-2.5 border border-[#E5E7EB] rounded-lg text-[14px] font-medium text-[#374151] hover:bg-[#F9FAFB] transition-colors"
          >
            {isReorderMode ? t('agentSettings.questionsReorderDoneButton') : t('agentSettings.questionsReorderButton')}
          </button>
        )}

        {!isReorderMode && (
          <>
            {/* Add Categories Button */}
            {isSuperAdmin && (
              <button
                onClick={() => setIsCategoryModalOpen(true)}
                className="px-4 py-2.5 border border-[#E5E7EB] rounded-lg text-[14px] font-medium text-[#374151] hover:bg-[#F9FAFB] transition-colors"
              >
                {t('agentSettings.questionsAddCategoriesButton')}
              </button>
            )}

            {/* Add Questions Button */}
            {isSuperAdmin && (
              <button
                onClick={() => setIsModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2.5 text-white rounded-lg text-[14px] font-medium hover:opacity-90 transition-colors"
                style={{ backgroundColor: aispaceColor }}
              >
                <Plus size={18} />
                {t('agentSettings.questionsAddQuestionButton')}
              </button>
            )}
          </>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-[#EEEEEE] overflow-hidden">
        {/* Table Header */}
        <div className="flex items-center bg-[#F5F5F5] border-b border-[#EEEEEE]">
          <div className="w-[50px] flex-shrink-0 py-3 px-4"></div>
          <div className="w-[140px] flex-shrink-0 py-3 pr-4">
            <span className="text-[13px] font-medium text-[#374151]">{t('agentSettings.questionsColCategories')}</span>
          </div>
          <div className="w-[280px] flex-shrink-0 py-3 pr-4">
            <span className="text-[13px] font-medium text-[#374151]">{t('agentSettings.questionsColTitle')}</span>
          </div>
          <div className="flex-1 py-3 pr-4">
            <span className="text-[13px] font-medium text-[#374151]">{t('agentSettings.questionsColDescription')}</span>
          </div>
          <div className="w-[50px] flex-shrink-0 py-3 px-4"></div>
        </div>

        {/* Table Body */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="w-8 h-8 animate-spin" style={{ color: aispaceColor }} />
          </div>
        ) : (isReorderMode ? questions : filteredQuestions).length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            {/* Icon Container */}
            <div className="w-16 h-16 bg-[#F3F4F6] rounded-xl flex items-center justify-center mb-5">
              <MessageSquare size={32} className="text-[#9CA3AF]" />
            </div>
            {/* Title */}
            <h3 className="text-[16px] font-semibold text-[#111827] mb-2">
              {t('agentSettings.questionsEmptyTitle')}
            </h3>
            {/* Description */}
            <p className="text-[14px] text-[#6B7280] text-center max-w-[320px]">
              {t('agentSettings.questionsEmptyHint')}
            </p>
          </div>
        ) : (
          <div className="relative">
            {reorderLoading && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-white bg-opacity-60">
                <Loader size="lg" color="gray" />
              </div>
            )}

            {isReorderMode ? (
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="sq-list">
                  {(provided) => (
                    <div {...provided.droppableProps} ref={provided.innerRef}>
                      {questions.map((question, index) => (
                        <Draggable key={question.id} draggableId={String(question.id)} index={index}>
                          {(provided) => renderRow(question, index, provided, index === questions.length - 1)}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            ) : (
              <div>
                {paginatedQuestions.map((question, index) => renderRow(question, index, null, index === paginatedQuestions.length - 1))}
              </div>
            )}
          </div>
        )}

        {/* Pagination */}
        {!isReorderMode && filteredQuestions.length > 0 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-[#EEEEEE]">
            <span className="text-[14px] text-[#6B7280]">
              {t('agentSettings.pageOf', { current: currentPage, total: totalPages || 1 })}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 text-[14px] font-medium text-[#374151] border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {t('agentSettings.previous')}
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage >= totalPages}
                className="px-4 py-2 text-[14px] font-medium text-[#374151] border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {t('agentSettings.next')}
              </button>
            </div>
          </div>
        )}
      </div>

      <AddQuestionModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        selectedQuestion={selectedQuestion}
        onSubmit={handleAddQuestion}
        categories={categories}
      />

      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setQuestionToDelete(null);
        }}
        onDelete={confirmDelete}
        title={t('agentSettings.questionsDeleteTitle')}
        message={t('agentSettings.questionsDeleteMessage')}
      />

      <AddCategoryModal
        isOpen={isCategoryModalOpen}
        onClose={() => setIsCategoryModalOpen(false)}
        existingCategories={categories}
        taskId={selectedTask?.id}
        onCategoriesChanged={fetchCategories}
      />
    </div>
  );
};

export default StandarQustions;
